/**
 * useWhatsappWindow - Composable compartilhado para cálculo da janela WhatsApp
 *
 * v6.4.7: Timer global único + cálculo 100% client-side
 *
 * Usa dados já existentes no payload das conversas (additional_attributes,
 * last_non_activity_message, meta.channel, can_reply). Zero queries adicionais.
 *
 * Timer global: 1 único setInterval para toda a aplicação.
 * Todos os componentes que usam este composable compartilham o mesmo `now`.
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const WINDOW_24H_MS = 24 * 60 * 60 * 1000;
const WINDOW_72H_MS = 72 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;
const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

// ── Timer Global Compartilhado ──────────────────────────────────
// 1 único setInterval para toda a aplicação, independente de quantos
// ConversationCards estão renderizados. Subscribers controlam lifecycle.
const globalNow = ref(Date.now());
let globalIntervalId = null;
let subscriberCount = 0;

function subscribe() {
  subscriberCount += 1;
  if (subscriberCount === 1 && !globalIntervalId) {
    globalNow.value = Date.now();
    globalIntervalId = setInterval(() => {
      globalNow.value = Date.now();
    }, 60000);
  }
}

function unsubscribe() {
  subscriberCount -= 1;
  if (subscriberCount <= 0) {
    subscriberCount = 0;
    if (globalIntervalId) {
      clearInterval(globalIntervalId);
      globalIntervalId = null;
    }
  }
}

// ── Composable Principal ────────────────────────────────────────
/**
 * @param {Object} options
 * @param {import('vue').Ref|import('vue').ComputedRef} options.conversation - Objeto da conversa (do Vuex store)
 * @param {import('vue').Ref|import('vue').ComputedRef} options.channelType - Tipo do canal (ex: 'Channel::Whatsapp')
 */
export function useWhatsappWindow({ conversation, channelType }) {
  // Lifecycle: registrar/desregistrar do timer global
  onMounted(() => subscribe());
  onBeforeUnmount(() => unsubscribe());

  // É canal WhatsApp API oficial?
  const isWhatsApp = computed(() => {
    const ct = channelType?.value || '';
    return ct === 'Channel::Whatsapp';
  });

  // É conversa de Click-to-WhatsApp (anúncio)?
  const isCTWA = computed(() => {
    const attrs = conversation?.value?.additional_attributes || {};
    return !!attrs.ctwa_clid;
  });

  // Calcula quando a janela expira
  const windowExpiresAt = computed(() => {
    const conv = conversation?.value;
    if (!conv) return null;

    // CTWA: Janela fixa de 72h baseada em ctwa_entered_at
    if (isCTWA.value) {
      const enteredAt =
        conv.additional_attributes?.ctwa_entered_at;
      if (!enteredAt) return null;
      const enteredMs = new Date(enteredAt).getTime();
      if (Number.isNaN(enteredMs)) return null;
      return enteredMs + WINDOW_72H_MS;
    }

    // Normal: Janela de 24h baseada na última mensagem incoming
    // Na lista, usamos last_non_activity_message se for incoming (type 0)
    // Na conversa aberta, usamos o array messages[]
    const messages = conv.messages || [];
    const lastNonActivity = conv.last_non_activity_message;

    let lastIncomingCreatedAt = null;

    // Tentar pelo array messages (quando conversa está aberta)
    if (messages.length >= 1) {
      const lastIncoming = [...messages]
        .reverse()
        .find(m => m.message_type === 0);
      if (lastIncoming) {
        lastIncomingCreatedAt = lastIncoming.created_at;
      }
    }

    // Fallback 1: last_non_activity_message (na lista de conversas)
    if (
      !lastIncomingCreatedAt &&
      lastNonActivity &&
      lastNonActivity.message_type === 0
    ) {
      lastIncomingCreatedAt = lastNonActivity.created_at;
    }

    // Fallback 2: messages[0] da lista (jbuilder retorna última msg)
    // Se a última mensagem é incoming, usá-la mesmo que não esteja em
    // last_non_activity_message
    if (
      !lastIncomingCreatedAt &&
      messages.length === 1 &&
      messages[0]?.message_type === 0
    ) {
      lastIncomingCreatedAt = messages[0].created_at;
    }

    if (!lastIncomingCreatedAt) return null;

    // created_at pode ser Unix timestamp (segundos) ou ISO string
    const createdMs =
      typeof lastIncomingCreatedAt === 'number'
        ? lastIncomingCreatedAt * 1000
        : new Date(lastIncomingCreatedAt).getTime();

    if (Number.isNaN(createdMs)) return null;

    return createdMs + WINDOW_24H_MS;
  });

  // Duração máxima da janela (para capar clock skew)
  const maxWindowMs = computed(() =>
    isCTWA.value ? WINDOW_72H_MS : WINDOW_24H_MS
  );

  // Tempo restante em milissegundos
  const remainingMs = computed(() => {
    if (!windowExpiresAt.value) return 0;
    const diff = windowExpiresAt.value - globalNow.value;
    if (diff <= 0) return 0;
    return Math.min(diff, maxWindowMs.value);
  });

  // Deve mostrar o indicador?
  const shouldShow = computed(() => {
    if (!isWhatsApp.value) return false;
    if (!conversation?.value?.can_reply) return false;
    if (!windowExpiresAt.value) return false;
    if (remainingMs.value <= 0) return false;
    return true;
  });

  // Nível de urgência: 'safe', 'warning', 'critical', ou null
  const urgencyLevel = computed(() => {
    if (!shouldShow.value) return null;
    const ms = remainingMs.value;
    if (ms <= ONE_HOUR_MS) return 'critical';
    if (ms <= SIX_HOURS_MS) return 'warning';
    return 'safe';
  });

  // Emoji indicador
  const urgencyIcon = computed(() => {
    const level = urgencyLevel.value;
    if (level === 'critical') return '🔴';
    if (level === 'warning') return '🟡';
    if (level === 'safe') return '🟢';
    return '';
  });

  // Texto formatado para tooltip
  const remainingText = computed(() => {
    if (!shouldShow.value) return '';
    const ms = remainingMs.value;
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const label = isCTWA.value
      ? 'Janela do anúncio fecha em'
      : 'Janela fecha em';

    if (hours > 0) {
      return `${label} ${hours}h ${minutes}min`;
    }
    return `${label} ${minutes}min`;
  });

  // Classes Tailwind para o container (usado no WhatsappWindowTimer)
  // v6.4.12: Sem bg opaco — compatível com wallpaper customizado via dashboard scripts
  const timerClasses = computed(() => {
    if (!shouldShow.value) return '';
    const level = urgencyLevel.value;
    if (level === 'critical') {
      return 'text-red-500 dark:text-red-400 animate-pulse-subtle';
    }
    if (level === 'warning') {
      return 'text-amber-600 dark:text-amber-400';
    }
    return 'text-emerald-600 dark:text-emerald-400';
  });

  return {
    isWhatsApp,
    isCTWA,
    shouldShow,
    remainingMs,
    remainingText,
    urgencyLevel,
    urgencyIcon,
    timerClasses,
  };
}
